import DashboardWrapper from "@/Components/Dashboard/DashboardWrapper";
import Icon from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import { useSetModal } from "@/shared/atoms/useModal";
import {
  lazy,
  Suspense,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type ReactQuillType from "react-quill";
import "react-quill/dist/quill.snow.css";
const ReactQuill = lazy(() => import("react-quill"));

const toolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  ["clean"],
];

const removeBase64Images = (_node: Node, delta) => {
  delta.ops = delta.ops.filter(
    (op) => !String(op.insert?.image ?? "").startsWith("data:")
  );
  return delta;
};

const withImageTransformation = (url: string) =>
  url.replace("/upload/", `/upload/c_limit,w_1200/f_auto/q_auto/`);

const CONTENT_STYLES = `
  h1 { font-size: 2em; font-weight: bold; }
  h2 { font-size: 1.5em; font-weight: bold; }
  h3 { font-size: 1.17em; font-weight: bold; }
  img { max-width: 100%; max-height: 600px; width: auto; height: auto; }
`;

const toHtmlDocument = (body: string) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${CONTENT_STYLES}</style></head><body>${body}</body></html>`;

const parseHtml = (html: string) =>
  new DOMParser().parseFromString(html, "text/html");

const isEmptyContent = (html: string) => {
  const { body } = parseHtml(html);
  return !body.textContent.trim() && !body.querySelector("img");
};

interface AboutUsProps {
  defaultValue?: string;
}

const AboutUs = ({ defaultValue }: AboutUsProps) => {
  const { t } = useTranslation("");
  const { register, setValue, control } = useFormContext();

  const setModal = useSetModal();
  const quillRef = useRef<ReactQuillType>(null);
  const [initialContent] = useState(() =>
    defaultValue ? parseHtml(defaultValue).body.innerHTML : ""
  );
  const [content, setContent] = useState(initialContent);
  const deferredContent = useDeferredValue(content);

  useEffect(() => {
    setValue(
      "about_us_content",
      isEmptyContent(deferredContent) ? "" : toHtmlDocument(deferredContent)
    );
  }, [deferredContent, register, setValue]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbar,
        handlers: {
          image: () => {
            const index =
              quillRef.current?.getEditor().getSelection(true)?.index ?? 0;
            setModal("aboutUsImage", {
              onUpload: (url: string) => {
                const editor = quillRef.current?.getEditor();
                editor?.insertEmbed(
                  index,
                  "image",
                  withImageTransformation(url),
                  "user"
                );
                editor?.setSelection(index + 1, 0, "user");
              },
            });
          },
        },
      },
      clipboard: {
        matchers: [["IMG", removeBase64Images]],
      },
    }),
    []
  );

  return (
    <div className="mb-12">
      <h2>{t("settings_page.aboutUs")}</h2>
      <DashboardWrapper>
        <div className="mb-[2rem] flex items-start gap-[1rem] rounded-[0.5rem] bg-grey-secondary p-[1.5rem]">
          <Icon className="h-8 w-8 shrink-0 fill-primary" variants="info" />
          <div>{t("settings_page.aboutUsInfo")}</div>
        </div>
        <div className="mb-[1rem] font-bold">
          {t("settings_page.aboutUsContent")}
        </div>

        <Suspense fallback={<Spinner size="0.25rem" />}>
          <Controller
            name="about_us_content"
            control={control}
            defaultValue={defaultValue}
            render={({ value }) => (
              <ReactQuill
                ref={quillRef}
                theme="snow"
                defaultValue={value}
                onChange={setContent}
                modules={modules}
                className="[&_.ql-editor]:min-h-120 [&_.ql-editor_img]:max-h-200"
              />
            )}
          />
        </Suspense>
      </DashboardWrapper>
    </div>
  );
};

export default AboutUs;
